import { apiClient } from './client';

export interface StreamUserToken {
  userId: string;
  token: string;
  apiKey: string;
}

export interface StreamChatUser {
  id: string;
  name?: string;
  full_name?: string;
  email?: string;
  role?: string;
}

export interface CreateChannelParams {
  type: 'messaging' | 'team' | 'commerce' | 'gaming' | 'livestream';
  name: string;
  memberIds: number[];
  celebrationId?: number;
  metadata?: Record<string, unknown>;
}

export interface StartDirectConversationParams {
  otherUserId: number;
  celebrationId?: number;
}

export interface ChannelInfo {
  channelId: string;
  channelType: string;
  cid: string;
}

export interface UserChannel {
  id: string;
  type: string;
  cid: string;
  name?: string;
  memberCount: number;
  lastMessageAt?: string;
  unreadCount: number;
  celebrationId?: number;
}

export interface RecentStreamMessage {
  id: string;
  channelId: string;
  channelType: string;
  channelCid: string;
  userId: string;
  userName: string;
  time: string;
  message: string;
  hasImage?: boolean;
}

/**
 * Stream Chat Service
 * Handles all Stream Chat API calls to backend
 */
class StreamChatService {
  /**
   * Get Stream Chat authentication token
   */
  async getUserToken(): Promise<StreamUserToken> {
    const response = await apiClient.get<{ success: boolean; data: StreamUserToken }>(
      '/stream-chat/token'
    );
    return response.data.data;
  }

  /**
   * Start a direct message conversation with another user
   */
  async startDirectConversation(params: StartDirectConversationParams): Promise<ChannelInfo> {
    const response = await apiClient.post<{ success: boolean; data: ChannelInfo }>(
      '/stream-chat/conversations/direct',
      params
    );
    return response.data.data;
  }

  /**
   * Create a new group channel
   */
  async createChannel(params: CreateChannelParams): Promise<ChannelInfo> {
    const response = await apiClient.post<{ success: boolean; data: ChannelInfo }>(
      '/stream-chat/channels',
      params
    );
    return response.data.data;
  }

  /**
   * Get user's channels
   */
  async getUserChannels(celebrationId?: number): Promise<UserChannel[]> {
    const params = celebrationId ? { celebrationId } : {};
    const response = await apiClient.get<{ success: boolean; data: UserChannel[] }>(
      '/stream-chat/channels',
      { params }
    );
    return response.data.data;
  }

  /**
   * Add members to a channel
   */
  async addMembersToChannel(
    channelType: string,
    channelId: string,
    memberIds: string[]
  ): Promise<void> {
    await apiClient.post('/stream-chat/channels/members/add', {
      channelType,
      channelId,
      memberIds,
    });
  }

  /**
   * Remove members from a channel
   */
  async removeMembersFromChannel(
    channelType: string,
    channelId: string,
    memberIds: string[]
  ): Promise<void> {
    await apiClient.post('/stream-chat/channels/members/remove', {
      channelType,
      channelId,
      memberIds,
    });
  }

  /**
   * Delete a channel
   */
  async deleteChannel(channelType: string, channelId: string): Promise<void> {
    await apiClient.delete(`/stream-chat/channels/${channelType}/${channelId}`);
  }

  /**
   * Search for users
   */
  async searchUsers(query: string, limit: number = 20): Promise<StreamChatUser[]> {
    const response = await apiClient.get<{ success: boolean; data: StreamChatUser[] }>(
      '/stream-chat/users/search',
      {
        params: { q: query, limit },
      }
    );
    return response.data.data;
  }

  /**
   * Get recent messages for right panel notifications
   */
  async getRecentMessages(limit: number = 5): Promise<RecentStreamMessage[]> {
    const response = await apiClient.get<{ success: boolean; data: RecentStreamMessage[] }>(
      '/stream-chat/recent-messages',
      {
        params: { limit }
      }
    );
    return response.data.data;
  }

  /**
   * Create celebration channels (auto-groups)
   */
  async createCelebrationChannels(celebrationId: number): Promise<void> {
    await apiClient.post(`/stream-chat/celebrations/${celebrationId}/channels`);
  }
}

export const streamChatService = new StreamChatService();
